<script lang="ts">
export interface EventReadyConfig<TData = unknown> {
  refresh: (config?: { purge: boolean }) => void;
  resetSelection: () => void;
  selectAll: () => void;
  setQuickFilterText: (text: string) => void;
  getTotalCount: () => number;
  redrawRows: () => void;
  refreshCellsByCriteria: (criteria: (node: IRowNode<TData>) => boolean, columns: string[]) => void;
}

import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  components: {
    ComponentProxy,
  },
});</script>

<script setup lang="ts" generic="TData extends object">
/* eslint-disable @typescript-eslint/unified-signatures */

import 'ag-grid-enterprise';
import { AgGridVue } from 'ag-grid-vue';
import AgTableStyles from './AgTableStyles.vue';
import { computed, inject, shallowRef } from 'vue';
import { throttle } from 'lodash';
import {
  ColDef,
  CsvExportParams,
  ExcelExportParams,
  ExportParams,
  FilterModel,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
  IServerSideDatasource,
  LoadSuccessParams,
  PaginationChangedEvent,
  RowClassParams,
  RowClassRules,
  SortModelItem,
} from 'ag-grid-enterprise';
import { ComponentProxy } from './columns/utils';
import LoggerService from '@/modules/common/services/logger.service';
import { watch, watchEffect } from 'vue';
import { persistColumnState, retrieveColumnState } from './column-state';
import {
  columnKeysExcl,
  processCsvCellCallback,
  processExcelCellCallback,
} from '@/modules/common/components/ag-table/export';
import { dataTypeDefinitions, setDefaultUseValueFormatterForExport } from './columns/common';
import { useSocketStore } from '@/store/socket-store';
import { useAuthStore } from '@/store/auth-store';

// @TODO: we should type provide/inject after we migrate all components to composition API
// (https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject)
const gridStateKey = inject<string | undefined>('gridStateKey', undefined);

const authStore = useAuthStore();

;

const props = withDefaults(
  defineProps<{
    tableId?: string;
    page: number;
    sort: SortModelItem;
    filter?: FilterModel;
    noSort?: boolean;
    noMenu?: boolean;
    noMove?: boolean;
    selectedItems?: TData[];
    pageSize: number;
    columnDefs: Array<ColDef<TData>>;
    getRowId: (data: TData) => string;
    getRowClass?: (params: RowClassParams<TData>) => string;
    queryData: (config: {
      page: number;
      pageSize: number;
      sort: SortModelItem;
      filter: FilterModel;
      signal: AbortSignal;
    }) => Promise<LoadSuccessParams | undefined>;
    summary?: TData[];
    pageSizeSelector?: number[];
    isRowSelectable?: (node: IRowNode<TData>) => boolean;
    rowClassRules?: RowClassRules<TData>;
    fitFullWidth?: boolean;
    autoGrow?: boolean;
    defaultExportParams?: ExportParams<never>;
  }>(),
  {
    tableId: undefined,
    selectedItems: () => [],
    summary: () => [],
    pageSizeSelector: () => [100, 500, 1000],
    isRowSelectable: (node) => node.data !== undefined,
    rowClassRules: undefined,
    fitFullWidth: false,
    autoGrow: false,
    filter: () => ({}),
    noSort: false,
    noMenu: false,
    noMove: false,
    defaultExportParams: undefined,
    getRowClass: undefined,
  }
);

const emit = defineEmits<{
  (event: 'ready', config: EventReadyConfig): void;
  (event: 'update:page', page: number): void;
  (event: 'update:page-size', pageSize: number): void;
  (event: 'update:sort', sort: SortModelItem): void;
  (event: 'update:filter', filter: FilterModel): void;
  (event: 'update:selected-items', items: TData[]): void;
}>();

let gridApi: GridApi | null = null;

const initialState = computed(() =>
  retrieveColumnState(authStore.user?.id, gridStateKey, props.tableId, props.columnDefs)
);

const autoSizeStrategy = computed(() => ({
  type: props.fitFullWidth ? 'fitGridWidth' : undefined,
}));

function onFirstDataRendered<TData>(params: FirstDataRenderedEvent<TData>) {
  if (initialState.value !== null || props.fitFullWidth) return;

  params.api.autoSizeAllColumns();

  const newColumnWidths = (params.api.getColumns() ?? [])
    .map((column) => {
      const colDef = column.getColDef();
      return colDef.initialWidth === undefined || column.getActualWidth() <= colDef.initialWidth
        ? undefined
        : { key: colDef, newWidth: colDef.initialWidth };
    })
    .filter(Boolean) as Array<{
    key: ColDef;
    newWidth: number;
  }>;

  params.api.setColumnWidths(newColumnWidths);
}

const showPagination = shallowRef(true);

let pageRestore: number | null = props.page;
const pageLocal = shallowRef(props.page);
watchEffect(() => (pageLocal.value = props.page));
watch(pageLocal, (page) => emit('update:page', page));

const pageSizeLocal = shallowRef(props.pageSize);
watchEffect(() => (pageSizeLocal.value = props.pageSize));
watch(pageSizeLocal, (pageSize) => emit('update:page-size', pageSize));

const sortLocal = shallowRef(props.sort);
watchEffect(() => (sortLocal.value = props.sort));
watch(sortLocal, (sort) => emit('update:sort', sort));

const filterLocal = shallowRef(props.filter);
watchEffect(() => (filterLocal.value = props.filter));
watch(filterLocal, (filter) => emit('update:filter', filter));

const localSelectedItems = shallowRef(props.selectedItems);
watchEffect(() => (localSelectedItems.value = props.selectedItems));
watch(localSelectedItems, (selectedItems) => emit('update:selected-items', selectedItems));

const totalCount = shallowRef(0);

const socketStore = useSocketStore();

// every time the socket (re)connects we want to re-fetch data
// because we might be looking at stale data
// Note: onOpen also fires immediately, but that will be before gridApi is assigned so we won't actually do anything
socketStore.onOpen(() => {
  gridApi?.refreshServerSide({ purge: true });
});

watch(
  () => props.queryData,
  () => gridApi?.refreshServerSide({ purge: true })
);

const paginationPageSizeSelector = computed(() =>
  [...props.pageSizeSelector, props.pageSize].filter((e, i, a) => a.indexOf(e) === i).sort()
);

const columnDefs = computed(() => {
  const colDefs = props.columnDefs.map((colDef) => {
    // apply our own props
    colDef.sortable = (colDef.sortable ?? true) && !props.noSort;
    colDef.suppressHeaderMenuButton ||= props.noMenu;
    colDef.suppressMovable ||= props.noMove;

    if (props.sort !== undefined) {
      const { colId, sort } = props.sort;
      if (colDef.colId === colId) {
        colDef.initialSort = sort;
      }
    }

    setDefaultUseValueFormatterForExport(colDef);

    return colDef;
  });

  const orderedColIds = new Map(
    initialState.value?.columnOrder?.orderedColIds.map((id, index) => [id, index])
  );
  if (orderedColIds.size) {
    // we have to manually sort the cols in the order they were saved
    // because when we apply initialState to AgGrid it doesn't respect ordering
    colDefs.sort((a, b) => {
      const aIndex = orderedColIds.get(a.colId ?? '');
      const bIndex = orderedColIds.get(b.colId ?? '');
      // one of them wasn't there, no swap needed
      if (aIndex === undefined || bIndex === undefined) return 0;
      return aIndex - bIndex;
    });
  }
  return colDefs;
});

const vueColumns = computed(() =>
  columnDefs.value.filter((cd) => cd.cellRendererSelector && cd.field).map((cd) => cd.field!)
);

const dataSource: IServerSideDatasource = {
  async getRows(params) {
    filterLocal.value = params.request.filterModel ?? {};
    sortLocal.value = params.request.sortModel.length ? params.request.sortModel[0] : props.sort;

    const loadParams = await fetch();

    // request was cancelled, there's another response arriving
    if (loadParams === undefined) return;

    showPagination.value =
      loadParams.rowCount === undefined ||
      loadParams.rowData.length < loadParams.rowCount ||
      loadParams.rowData.length > paginationPageSizeSelector.value[0];

    params.success(loadParams);
    totalCount.value = loadParams.rowCount as number;

    const isGridApiDestroyed = gridApi?.isDestroyed() === true;

    if (!isGridApiDestroyed) {
      if (loadParams.rowData.length > 0) {
        gridApi?.hideOverlay();
      } else {
        gridApi?.showNoRowsOverlay();
      }
    }

    if (pageRestore !== null) {
      if (!isGridApiDestroyed) {
        gridApi?.paginationGoToPage(pageRestore - 1);
      }
      pageRestore = null;
    }
  },
};

function refreshCellsByCriteria(
  criteria: (node: IRowNode<TData>) => boolean,
  columns: string[]
): void {
  if (!gridApi || gridApi.isDestroyed()) return;

  const rowNodes: Array<IRowNode<TData>> = [];
  gridApi.forEachNode((node) => {
    if (criteria(node)) {
      rowNodes.push(node);
    }
  });

  if (rowNodes.length > 0) {
    gridApi.refreshCells({
      rowNodes,
      columns,
      force: false,
    });
  }
}

function onGridReady(params: GridReadyEvent): void {
  gridApi = params.api;

  const refresh = throttle(
    (config?: { purge: boolean }) => params.api.refreshServerSide(config),
    500,
    {
      leading: true,
      trailing: true, // because we want the most recent data
    }
  );

  emit('ready', {
    refresh,
    resetSelection: () => gridApi?.deselectAll(),
    selectAll: () => gridApi?.selectAll(),
    setQuickFilterText: (text) => gridApi?.setGridOption('quickFilterText', text),
    getTotalCount: () => totalCount.value,
    redrawRows: () => gridApi?.redrawRows(),
    refreshCellsByCriteria,
  });
}

function onFilterChanged() {
  gridApi?.deselectAll();
  localSelectedItems.value = [];
  updateSelectedItems();
}

let abortController: AbortController | null = null;
async function fetch(): Promise<LoadSuccessParams | undefined> {
  // we want to initiate a new request, but the previous one is still pending
  // cancel to avoid stale responses arriving late (and potentially in the wrong order)
  abortController?.abort();
  abortController = new AbortController();

  pageLocal.value = pageRestore ?? pageLocal.value;
  try {
    return props.queryData({
      signal: abortController.signal,
      sort: sortLocal.value,
      filter: filterLocal.value,
      page: pageLocal.value,
      pageSize: pageSizeLocal.value,
    });
  } catch (e) {
    LoggerService.warn(e);
  }
}

function onStoreRefreshed(): void {
  // we need to update the summary
  updateSelectedItems();

  // this is required to trigger vue components re-render (like status chip)
  gridApi?.refreshCells({
    force: true,
    columns: vueColumns.value,
  });
}

function updateSelectedItems(): void {
  if (gridApi === null) return;

  // we want to modify the existing selection to keep it on page change
  // at some rare cases we may have two different records sharing a single id,
  // so using map here to get rid of the ghost nodes being selected
  const selectedItems = new Map(
    localSelectedItems.value.map((item) => [props.getRowId(item), item])
  );

  gridApi.forEachNode((node) => {
    if (node.data === undefined) return;
    if (node.isSelected()) {
      selectedItems.set(props.getRowId(node.data), node.data);
    } else {
      // we only remove existing, but not selected nodes
      selectedItems.delete(props.getRowId(node.data));
    }
  });
  localSelectedItems.value = [...selectedItems.values()];
}

function onSelectionChanged(): void {
  updateSelectedItems();
}

function onPaginationChanged(ev: PaginationChangedEvent): void {
  if (ev.newPage) {
    pageLocal.value = (gridApi?.paginationGetCurrentPage() ?? 0) + 1;
    gridApi?.deselectAll();
    localSelectedItems.value = [];
  }
  if (ev.newPageSize) {
    // invalidate cache
    gridApi?.setGridOption('cacheBlockSize', 0);
    pageSizeLocal.value = gridApi?.paginationGetPageSize() ?? paginationPageSizeSelector.value[0];
    gridApi?.deselectAll();
    localSelectedItems.value = [];
  }

  // fires after the page nodes are loaded
  if (ev.keepRenderedRows) {
    updateSelectedItems();
  }
}

function saveColumnState(): void {
  persistColumnState(authStore.user?.id, gridStateKey, gridApi, props.tableId);
}

function getVisibleColumnDefs() {
  return (gridApi?.getAllDisplayedColumns() ?? []).map((def) => def.getColDef());
}

const excelExportParams = computed(() => {
  return {
    author: 'Provable Markets',
    sheetName: 'export',
    // we exclude some common columns from the export
    columnKeys: columnKeysExcl(getVisibleColumnDefs(), 'actions', 'checkbox'),
    processCellCallback: processExcelCellCallback,
    ...props.defaultExportParams,
  } as ExcelExportParams;
});

const csvExportParams = computed(() => {
  return {
    // we exclude some common columns from the export
    columnKeys: columnKeysExcl(getVisibleColumnDefs(), 'actions', 'checkbox'),
    processCellCallback: processCsvCellCallback,
    ...props.defaultExportParams,
  } as CsvExportParams;
});
</script>

<template>
  <AgTableStyles v-slot="slotProps" :data-test-id="tableId">
    <!--
      We pass :max-blocks-in-cache="1" because in SSRM we can't use "headerCheckboxSelectionCurrentPageOnly" (v31)
      the normal SSRM select all behavior is to select all items that have been cached.
      Because we also pass :cache-block-size="pageSize", using :max-blocks-in-cache="1"
      has the effect of only caching the current page (i.e. no cache at all)
    -->
    <AgGridVue
      v-bind="slotProps"
      :auto-size-strategy="initialState === null ? autoSizeStrategy : undefined"
      :cache-block-size="pageSize"
      :column-defs="columnDefs"
      :data-type-definitions="dataTypeDefinitions"
      :default-csv-export-params="csvExportParams"
      :default-excel-export-params="excelExportParams"
      :dom-layout="autoGrow ? 'autoHeight' : 'normal'"
      :get-row-class="getRowClass"
      :get-row-id="(params) => getRowId(params.data)"
      :initial-state="initialState"
      :is-row-selectable="isRowSelectable"
      maintain-column-order
      :max-blocks-in-cache="1"
      :pagination="showPagination"
      :pagination-page-size="pageSize"
      :pagination-page-size-selector="paginationPageSizeSelector"
      :pinned-bottom-row-data="summary"
      :row-class-rules="rowClassRules"
      row-model-type="serverSide"
      :server-side-datasource="dataSource"
      v-on="$listeners"
      @column-moved="saveColumnState"
      @column-pinned="saveColumnState"
      @column-resized="saveColumnState"
      @column-visible="saveColumnState"
      @filter-changed="onFilterChanged"
      @first-data-rendered="onFirstDataRendered"
      @grid-ready="onGridReady"
      @pagination-changed="onPaginationChanged"
      @selection-changed="onSelectionChanged"
      @sort-changed="saveColumnState"
      @store-refreshed="onStoreRefreshed"
    />
  </AgTableStyles>
</template>

<style lang="scss" scoped></style>
